<script setup lang="ts">

import { APP_ROUTES, useCurrentSignatureRequestKey } from '~~core';
import { BrandButton, SecureEnvironment, useFetchBrand } from '~~base-ui';

import { GoneAlreadySignedContent, GoneGenericContent } from '../../../components';
import { useFetchFeedbackInfo } from '../../../repositories/feedback';

const isReady = ref(false);
const { signatureRequestKey } = useCurrentSignatureRequestKey();
const { feedback, isFetched } = useFetchFeedbackInfo(signatureRequestKey.value);
const { brand } = useFetchBrand(signatureRequestKey);

const validateFeedback = () => {
  const status = feedback.value?.status;
  const signer = feedback.value?.signer;

  const error = (!signer?.signable && !signer?.signed) && status === 'running';

  if (error) throw createError({ statusCode: 404, fatal: true });

  isReady.value = true;
};

watch(isFetched, (value: boolean) => {
  if (value) validateFeedback();
});

const validSignatureRequestStatus = computed(
  () => ['closed', 'running'].includes(feedback.value?.status ?? 'unknow'),
);
const signerAlreadySigned = computed(() => feedback.value?.signer.signed);
const isFullySigned = computed(() => feedback.value?.status === 'closed');

const hasAccount = computed(() => !!feedback.value?.signer.isUser);

const CTALink = computed(() => {
  const queryString = signerAlreadySigned.value ? 'tab=signed' : 'tab=pending';

  if (!hasAccount.value) return APP_ROUTES.signup;

  return `${APP_ROUTES.signerArea}?${queryString}`;
});

</script>

<template>
  <div
    v-show="isReady"
    :class="$style.root"
  >
    <GoneAlreadySignedContent
      v-if="signerAlreadySigned && validSignatureRequestStatus"
      :is-fully-signed="isFullySigned"
    />

    <GoneGenericContent
      v-else
      :sender-email="feedback?.senderEmail"
    />

    <BrandButton
      :class="$style.actionButton"
      :to="CTALink"
      data-testid="accessDocumentsButton"
      external
    >
      {{ $t('requirement.gone.accessDocuments') }}
    </BrandButton>

    <SecureEnvironment :hidden-clicksign-name="!!brand" />
  </div>
</template>

<style lang="scss" module>
.root {
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: var(--space-large-xx) var(--space-regular);

  .actionButton {
    margin-bottom: var(--space-small-xx);
  }
}
</style>
